import React, { useEffect, useMemo, useState } from 'react'
import {
  Redirect,
  Route,
  Switch,
  useLocation
} from 'react-router-dom'
import { Col, Container, Row } from 'react-bootstrap'

import { Routes } from 'global/routes'
import { GlobalProvider } from 'global/Context'
import { useCourseStatus } from 'global/hooks/useUserCouseStatus'
import { useRightSidebar } from 'global/hooks/useRightSidebar'
import { useFeatureFlags } from 'global/hooks/useFeatureFlags'
import { FEATURE_FLAG_KEYS } from 'global/hooks/useFeatureFlags/constants'

import memberMenuLinks from 'utils/helpers/memberMenuLinks'
import { CommonRoutes } from 'utils/helpers/commonRoutes'

import Header from 'components/DashboardWrapper/Header'
import LeftSideBar from 'components/DashboardWrapper/LeftNav'
import PageLoader from 'components/PageLoader'
import {
  DASHBOARD_BASE_URLS,
  COURSE_COMPONENTS
} from 'pages/Member/constants'

import Dashboard from './Dashboard'
import CoachingSessions from './CoachingSessions'
import CoachingSessionsCollab from './CoachingSessionsCollab'
import OKR from './OKR'
import Workshops from './Workshops'
import ResourceLibrary from './ResourceLibrary'
import LeadershipInAction from './LeadershipInAction'
import Settings from './Settings'
import styles from './styles.module.scss'

const {
  COACHING_SESSIONS,
  DASHBOARD,
  OBJECTIVES_AND_KEY_RESULTS,
  WORKSHOPS,
  RESOURCE_LIBRARY,
  LEADERSHIP_IN_ACTION,
  LEADERSHIP_IN_ACTION_PROFILE,
  SETTINGS
} = Routes

const SwitchRoutes = () => (
  <Switch>
    <Route path={DASHBOARD} component={Dashboard} />
    <Route
      exact
      path={`${COACHING_SESSIONS}/legacy`}
      component={CoachingSessions}
    />
    <Route
      exact
      path={`${COACHING_SESSIONS}/collab`}
      render={() => <Redirect to={COACHING_SESSIONS} />}
    />
    <Route
      exact
      path={COACHING_SESSIONS}
      component={CoachingSessionsCollab}
    />
    <Route
      exact
      path={OBJECTIVES_AND_KEY_RESULTS}
      component={OKR}
    />
    <Route exact path={WORKSHOPS} component={Workshops} />
    <Route
      exact
      path={RESOURCE_LIBRARY}
      component={ResourceLibrary}
    />
    <Route
      exact
      path={LEADERSHIP_IN_ACTION_PROFILE}
      component={LeadershipInAction}
    />
    <Route
      exact
      path={LEADERSHIP_IN_ACTION}
      component={LeadershipInAction}
    />
    <Route path={SETTINGS} component={Settings} />
    <CommonRoutes />
  </Switch>
)

const Member = () => {
  const { pathname } = useLocation()
  const { courseStatusData } = useCourseStatus()
  const { sideBarData } = useRightSidebar()

  const [headerTitle, setHeaderTitle] = useState('')
  const [actionSteps, setActionSteps] = useState(null)
  const [menuLinks, setMenuLinks] = useState(memberMenuLinks)
  const { isEnabled: isFeatureEnabled } = useFeatureFlags()

  // Desktop left-nav: always show Coaching Sessions; hide Workshops if none exist.
  useEffect(() => {
    const workshopData = Array.isArray(sideBarData)
      ? sideBarData.find(x => x?.type === 'workshop')
      : null
    const hasWorkshops =
      Array.isArray(workshopData?.steps) &&
      workshopData.steps.length > 0

    const leadershipEnabled = isFeatureEnabled(
      FEATURE_FLAG_KEYS.LEADERSHIP_IN_ACTION
    )

    setMenuLinks(
      memberMenuLinks.filter(link => {
        if (link.to === '/workshops' && !hasWorkshops)
          return false
        if (
          link.to === LEADERSHIP_IN_ACTION &&
          !leadershipEnabled
        )
          return false
        return true
      })
    )
  }, [sideBarData, isFeatureEnabled])

  const nextStageTo = useMemo(() => {
    if (
      !Array.isArray(courseStatusData) ||
      courseStatusData.length === 0
    ) {
      return `${DASHBOARD}/plp`
    }

    const firstIncomplete = courseStatusData.find(
      course => course?.status === false
    )

    const targetTitle = firstIncomplete?.title
    const target = COURSE_COMPONENTS.find(
      component => component.title === targetTitle
    )

    return target?.to || `${DASHBOARD}/plp`
  }, [courseStatusData])

  if (DASHBOARD_BASE_URLS.includes(pathname)) {
    // Wait until course status is loaded so we can land the user on the
    // correct "current stage" instead of always defaulting to PLP.
    if (!courseStatusData) {
      return (
        <PageLoader loader>
          <div />
        </PageLoader>
      )
    }

    return <Redirect to={nextStageTo} />
  }

  return (
    <GlobalProvider
      value={{
        setHeaderTitle,
        actionSteps,
        setActionSteps,
        setMenuLinks
      }}
    >
      <Container className={styles.container}>
        <Header title={headerTitle} />
        <Row className="h-100">
          <LeftSideBar links={menuLinks} />
          <Col md={10} xs={12} className="bg-gray p-0">
            <SwitchRoutes />
          </Col>
        </Row>
      </Container>
    </GlobalProvider>
  )
}

Member.propTypes = {}

export default Member
